import React, { useEffect, useRef, useState } from 'react';
import { animalApi, farmApi, stableApi, boxApi, animalFormOptionsApi } from '../services/api';
import { Animal, Farm, Stable, Box, MasterDataValue } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import AnimalCard from '../components/AnimalCard';
import ExportAnimalsModal from '../components/ExportAnimalsModal';
import Pagination from '../components/Pagination';
import ClearableInput from '../components/ClearableInput';
import ClearableSelect from '../components/ClearableSelect';
import ConfirmModal from '../components/ConfirmModal';

interface AnimalsFilters {
  status?: string;
  gender?: string;
  breed?: string;
  stable_id?: number;
  box_id?: number;
  cattle_document_received?: string;
  cattle_passport_status?: string;
}

const Animals: React.FC = () => {
  const { t } = useLanguage();
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [selectedFarmId, setSelectedFarmId] = useState<number | null>(null);
  const [stables, setStables] = useState<Stable[]>([]);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [breeds, setBreeds] = useState<MasterDataValue[]>([]);
  const [colors, setColors] = useState<MasterDataValue[]>([]);
  const [countries, setCountries] = useState<MasterDataValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AnimalsFilters>({ status: 'aktiv' });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState<Animal | null>(null);
  const [selectedAnimalId, setSelectedAnimalId] = useState<number | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showMoveForm, setShowMoveForm] = useState(false);
  const [movingAnimal, setMovingAnimal] = useState<Animal | null>(null);
  const [moveData, setMoveData] = useState({
    to_box_id: '',
    movement_type: 'transfer',
    reason: ''
  });
  // Move modal independent selectors
  const [moveFarms, setMoveFarms] = useState<Farm[]>([]);
  const [moveStables, setMoveStables] = useState<Stable[]>([]);
  const [moveBoxes, setMoveBoxes] = useState<Box[]>([]);
  const [moveFarmId, setMoveFarmId] = useState<number | null>(null);
  const [moveStableId, setMoveStableId] = useState<number | null>(null);
  const [deleteAnimalId, setDeleteAnimalId] = useState<number | null>(null);
  const [newAnimal, setNewAnimal] = useState({
    ear_tag: '',
    birth_date: '',
    arrival_date: new Date().toISOString().split('T')[0], // Aktuális dátum alapértelmezettként
    gender: 'him' as 'him' | 'nosteny',
    breed: '',
    color: '',
    mother_ear_tag: '',
    origin_country: '',
    origin_farm_code: '',
    purchase_weight: '',
    purchase_price: '',
    invoice_number: '',
    sale_invoice_number: '',
    departure_date: '',
    cattle_document_received: false,
    daily_weight_gain: '',
    status: 'aktiv' as const
  });

  const loadAnimals = async (page: number = currentPage) => {
    try {
      setLoading(true);
      const params: Record<string, any> = { page };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.status) params.status = filters.status;
      if (filters.gender) params.gender = filters.gender;
      if (filters.breed) params.breed = filters.breed;
      if (selectedFarmId) params.farm_id = selectedFarmId;
      if (filters.stable_id) params.stable_id = filters.stable_id;
      if (filters.box_id) params.box_id = filters.box_id;
      if (filters.cattle_document_received) params.cattle_document_received = filters.cattle_document_received;
      if (filters.cattle_passport_status) params.cattle_passport_status = filters.cattle_passport_status;
      params.sort_by = sortBy;
      params.sort_direction = sortDirection;

      const response = await animalApi.getAll(params);
      setAnimals(response.data);
      setLastPage(response.last_page);
      setPerPage(response.per_page);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading animals:', error);
      handleApiError(error, 'Hiba történt az állatok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const loadFarms = async () => {
    try {
      const response = await farmApi.getAll();
      setFarms(response);
    } catch (error) {
      console.error('Error loading farms:', error);
      handleApiError(error, 'Hiba történt a telepek betöltésekor');
    }
  };

  const loadBoxes = async (farmId: number) => {
    try {
      const boxesData = await boxApi.getAll(farmId);
      setBoxes(boxesData);
    } catch (error) {
      console.error('Error loading boxes:', error);
      handleApiError(error, 'Hiba történt a boxok betöltésekor');
    }
  };

  const loadStables = async (farmId: number) => {
    try {
      const stablesData = await stableApi.getAll(farmId);
      setStables(stablesData);
    } catch (error) {
      console.error('Error loading stables:', error);
      handleApiError(error, 'Hiba történt az istállók betöltésekor');
    }
  };

  const loadBreeds = async () => {
    try {
      const data = await animalFormOptionsApi.getBreeds();
      setBreeds(data || []);
    } catch (error) {
      console.error('Error loading breeds:', error);
      handleApiError(error, 'Hiba történt a fajták betöltésekor');
    }
  };

  const loadColors = async () => {
    try {
      const data = await animalFormOptionsApi.getColors();
      setColors(data || []);
    } catch (error) {
      console.error('Error loading colors:', error);
      handleApiError(error, 'Hiba történt a színek betöltésekor');
    }
  };

  const loadCountries = async () => {
    try {
      const data = await animalFormOptionsApi.getCountries();
      setCountries(data || []);
    } catch (error) {
      console.error('Error loading countries:', error);
      handleApiError(error, 'Hiba történt az országok betöltésekor');
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch animals when page, filters, or debounced search change.
  // Resets to page 1 when filters/search change (without double-fetching).
  const prevDeps = useRef({ filters, debouncedSearch, selectedFarmId, sortBy, sortDirection });
  const isInitialMount = useRef(true);
  useEffect(() => {
    const filtersChanged = !isInitialMount.current && (
      prevDeps.current.filters !== filters ||
      prevDeps.current.debouncedSearch !== debouncedSearch ||
      prevDeps.current.selectedFarmId !== selectedFarmId ||
      prevDeps.current.sortBy !== sortBy ||
      prevDeps.current.sortDirection !== sortDirection
    );
    prevDeps.current = { filters, debouncedSearch, selectedFarmId, sortBy, sortDirection };
    isInitialMount.current = false;

    if (filtersChanged && currentPage !== 1) {
      setCurrentPage(1); // Will re-trigger this effect with page 1
      return;
    }

    loadAnimals(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, debouncedSearch, selectedFarmId, sortBy, sortDirection]);

  // Load reference data once on mount
  useEffect(() => {
    loadFarms();
    loadBreeds();
    loadColors();
    loadCountries();
  }, []);

  // Load stables and boxes when farm changes
  useEffect(() => {
    if (selectedFarmId) {
      loadStables(selectedFarmId);
      loadBoxes(selectedFarmId);
    } else {
      setStables([]);
      setBoxes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFarmId]);

  // Filter box options by selected stable
  const filteredBoxOptions = filters.stable_id
    ? boxes.filter(b => b.stable_id === filters.stable_id)
    : boxes;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aktiv': return 'bg-green-100 text-green-800';
      case 'eladott': return 'bg-blue-100 text-blue-800';
      case 'levagott': return 'bg-yellow-100 text-yellow-800';
      case 'elhullott': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleAddAnimal = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { purchase_weight, purchase_price, daily_weight_gain, ...restAnimal } = newAnimal;
      const animalData = {
        ...restAnimal,
        purchase_weight: parseFloat(purchase_weight) || undefined,
        purchase_price: parseFloat(purchase_price) || undefined,
        daily_weight_gain: parseFloat(daily_weight_gain) || 1.1,
      };
      
      await animalApi.create(animalData);
      showSuccess('Állat sikeresen hozzáadva');
      setShowAddForm(false);
      setNewAnimal({
        ear_tag: '',
        birth_date: '',
        arrival_date: new Date().toISOString().split('T')[0],
        gender: 'him',
        breed: '',
        color: '',
        mother_ear_tag: '',
        origin_country: '',
        origin_farm_code: '',
        purchase_weight: '',
        purchase_price: '',
        invoice_number: '',
        sale_invoice_number: '',
        departure_date: '',
        cattle_document_received: false,
        daily_weight_gain: '',
        status: 'aktiv'
      });
      loadAnimals();
    } catch (error: any) {
      console.error('Error creating animal:', error);
      handleApiError(error, 'Hiba történt az állat létrehozásakor');
    }
  };

  const handleEditAnimal = (animal: Animal) => {
    setEditingAnimal(animal);
    setShowEditForm(true);
  };

  const handleUpdateAnimal = async () => {
    if (!editingAnimal) return;
    
    try {
      const animalData = {
        ...editingAnimal,
        purchase_weight: editingAnimal.purchase_weight || undefined,
        purchase_price: editingAnimal.purchase_price || undefined,
        daily_weight_gain: editingAnimal.daily_weight_gain || undefined,
      };
      
      await animalApi.update(editingAnimal.id, animalData);
      showSuccess('Állat adatai sikeresen frissítve');
      setShowEditForm(false);
      setEditingAnimal(null);
      loadAnimals();
    } catch (error: any) {
      console.error('Error updating animal:', error);
      handleApiError(error, 'Hiba történt az állat módosításakor');
    }
  };

  const handleDeleteAnimal = async () => {
    if (deleteAnimalId === null) return;

    try {
      await animalApi.delete(deleteAnimalId);
      showSuccess('Állat sikeresen törölve');
      loadAnimals();
    } catch (error) {
      console.error('Error deleting animal:', error);
      handleApiError(error, t('delete_animal_error', 'animals'));
    } finally {
      setDeleteAnimalId(null);
    }
  };

  const handleMoveAnimal = async (animal: Animal) => {
    setMovingAnimal(animal);
    setMoveData({ to_box_id: '', movement_type: 'transfer', reason: '' });
    setMoveStables([]);
    setMoveBoxes([]);
    setMoveFarmId(null);
    setMoveStableId(null);
    setShowMoveForm(true);

    try {
      const farmsData = await farmApi.getAll();
      setMoveFarms(farmsData);
    } catch (error) {
      console.error('Error loading farms for move:', error);
      handleApiError(error, 'Hiba történt a telepek betöltésekor');
    }
  };

  const handleMoveFarmChange = async (farmId: number | null) => {
    setMoveFarmId(farmId);
    setMoveStableId(null);
    setMoveData(prev => ({ ...prev, to_box_id: '' }));
    setMoveStables([]);
    setMoveBoxes([]);

    if (!farmId) return;

    try {
      const [stablesData, boxesData] = await Promise.all([
        stableApi.getAll(farmId),
        boxApi.getAll(farmId)
      ]);
      setMoveStables(stablesData);
      setMoveBoxes(boxesData);
    } catch (error) {
      console.error('Error loading stables/boxes for move:', error);
      handleApiError(error, 'Hiba történt az istállók/boxok betöltésekor');
    }
  };

  const handleMoveStableChange = (stableId: number | null) => {
    setMoveStableId(stableId);
    setMoveData(prev => ({ ...prev, to_box_id: '' }));
  };

  const filteredMoveBoxes = moveStableId
    ? moveBoxes.filter(b => b.stable_id === moveStableId)
    : moveBoxes;

  const handleConfirmMove = async () => {
    if (!movingAnimal || !moveData.to_box_id) return;
    
    try {
      await animalApi.moveAnimal(movingAnimal.id, {
        to_box_id: parseInt(moveData.to_box_id),
        movement_type: moveData.movement_type,
        reason: moveData.reason
      });
      setShowMoveForm(false);
      setMovingAnimal(null);
      loadAnimals();
      showSuccess(t('animal_moved_success', 'animals'));
    } catch (error) {
      console.error('Error moving animal:', error);
      handleApiError(error, t('animal_move_error', 'animals'));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('animals', 'menu')}</h1>
            <p className="mt-2 text-gray-600">
              {t('animals_management_desc', 'animals')}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowExportModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('add_new_animal', 'animals')}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg mb-6">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('search', 'action')}
              </label>
              <ClearableInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={t('search_placeholder', 'animals')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telep
              </label>
              <ClearableSelect
                value={String(selectedFarmId || '')}
                onChange={(val) => {
                  setSelectedFarmId(val ? parseInt(val) : null);
                  setFilters(prev => ({ ...prev, stable_id: undefined, box_id: undefined }));
                }}
                placeholder="Összes telep"
              >
                {farms.map(farm => (
                  <option key={farm.id} value={farm.id}>{farm.name}</option>
                ))}
              </ClearableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Istálló
              </label>
              <ClearableSelect
                value={String(filters.stable_id || '')}
                onChange={(val) => setFilters({...filters, stable_id: val ? parseInt(val) : undefined, box_id: undefined})}
                placeholder="Összes istálló"
                disabled={!selectedFarmId}
              >
                {stables.map(stable => (
                  <option key={stable.id} value={stable.id}>{stable.name}</option>
                ))}
              </ClearableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Box
              </label>
              <ClearableSelect
                value={String(filters.box_id || '')}
                onChange={(val) => setFilters({...filters, box_id: val ? parseInt(val) : undefined})}
                placeholder="Összes box"
                disabled={!selectedFarmId}
              >
                {filteredBoxOptions.map(box => (
                  <option key={box.id} value={box.id}>{box.name}</option>
                ))}
              </ClearableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('status', 'form')}
              </label>
              <ClearableSelect
                value={filters.status || ''}
                onChange={(val) => setFilters({...filters, status: val || undefined})}
                placeholder={t('all_statuses', 'animals')}
              >
                <option value="aktiv">{t('active', 'status')}</option>
                <option value="eladott">{t('sold', 'status')}</option>
                <option value="levagott">{t('slaughtered', 'status')}</option>
                <option value="elhullott">{t('deceased', 'status')}</option>
              </ClearableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('gender', 'animals')}
              </label>
              <ClearableSelect
                value={filters.gender || ''}
                onChange={(val) => setFilters({...filters, gender: val || undefined})}
                placeholder={t('all_genders', 'animals')}
              >
                <option value="him">{t('male', 'animal')}</option>
                <option value="nosteny">{t('female', 'animal')}</option>
              </ClearableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('breed', 'animals')}
              </label>
              <ClearableSelect
                value={filters.breed || ''}
                onChange={(val) => setFilters({...filters, breed: val || undefined})}
                placeholder={t('all_breeds', 'animals')}
              >
                {breeds.map(breed => (
                  <option key={breed.key} value={breed.value}>{breed.display_value || breed.value}</option>
                ))}
              </ClearableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marhalevél státusz
              </label>
              <ClearableSelect
                value={filters.cattle_passport_status || ''}
                onChange={(val) => setFilters({...filters, cattle_passport_status: val || undefined})}
                placeholder="Összes"
              >
                <option value="complete">✓ Teljes (2/2)</option>
                <option value="incomplete">⚠ Hiányos (0-1/2)</option>
                <option value="missing">✗ Hiányzik (0/2)</option>
              </ClearableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rendezés
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
              >
                <option value="arrival_date">Érkezési dátum</option>
                <option value="breed">Fajta</option>
                <option value="ear_tag">Fülszám</option>
                <option value="created_at">Rögzítés dátuma</option>
                <option value="birth_date">Születési dátum</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Irány
              </label>
              <button
                type="button"
                onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 text-sm text-gray-700 flex items-center justify-center gap-2"
              >
                {sortDirection === 'asc' ? '↑ Növekvő' : '↓ Csökkenő'}
              </button>
            </div>

            <div>
              <button
                onClick={() => {
                  setFilters({});
                  setSearchTerm('');
                }}
                className="w-full mt-6 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
              >
                {t('clear_filters', 'action')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md relative">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('animals_list', 'animals')} ({total})
          </h3>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-60 z-10 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('ear_tag', 'animal')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Marhalevél
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('breed', 'animals')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('gender', 'animals')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('birth_date', 'animals')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('current_weight', 'animals')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('status', 'form')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('current_box', 'animals')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('slaughterable', 'animals')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions', 'animals')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {animals.map((animal) => (
                <tr key={animal.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => setSelectedAnimalId(animal.id)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline cursor-pointer"
                    >
                      {animal.ear_tag}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const passportStatus = (animal as any).calculated_data?.cattle_passport_status;
                      const statusColor = passportStatus?.status_color || 'red';
                      const bgColor = statusColor === 'green' ? 'bg-green-100' : statusColor === 'orange' ? 'bg-orange-100' : 'bg-red-100';
                      const textColor = statusColor === 'green' ? 'text-green-800' : statusColor === 'orange' ? 'text-orange-800' : 'text-red-800';
                      const dotColor = statusColor === 'green' ? 'bg-green-500' : statusColor === 'orange' ? 'bg-orange-500' : 'bg-red-500';

                      return (
                        <div className={`inline-flex items-center px-2.5 py-1 rounded-full ${bgColor}`}>
                          <span className={`h-2 w-2 rounded-full ${dotColor} mr-2`}></span>
                          <span className={`text-xs font-medium ${textColor}`}>
                            {passportStatus?.has_original && passportStatus?.has_own ? '2/2' :
                             passportStatus?.has_original || passportStatus?.has_own ? '1/2' : '0/2'}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{animal.breed}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {animal.gender === 'him' ? t('male', 'animal') : t('female', 'animal')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(animal.birth_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {(animal as any).calculated_data?.estimated_current_weight ?
                        `${(animal as any).calculated_data.estimated_current_weight} ${t('kg', 'general')}` :
                        '-'
                      }
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(animal.status)}`}>
                      {animal.status.charAt(0).toUpperCase() + animal.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {(animal as any).current_box?.name || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(animal as any).calculated_data?.can_be_slaughtered ? (
                      <span className="text-green-600 font-medium">{t('yes', 'general')}</span>
                    ) : (
                      <span className="text-red-600">{t('no', 'general')}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedAnimalId(animal.id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        {t('card', 'action')}
                      </button>
                      <button
                        onClick={() => handleMoveAnimal(animal)}
                        className="text-green-600 hover:text-green-900"
                        title={t('move_animal_tooltip', 'animals')}
                      >
                        {t('move', 'action')}
                      </button>
                      <button
                        onClick={() => handleEditAnimal(animal)}
                        className="text-yellow-600 hover:text-yellow-900"
                      >
                        {t('edit', 'action')}
                      </button>
                      <button
                        onClick={() => setDeleteAnimalId(animal.id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('delete_animal_tooltip', 'animals')}
                      >
                        {t('delete', 'action')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {animals.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m6 0h.01M10 13h.01M16 13h.01" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('no_animals', 'animals')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('no_animals_matching_filters', 'animals')}
            </p>
          </div>
        )}

        {/* Pagination */}
        {lastPage > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="text-sm text-gray-700">
              {((currentPage - 1) * perPage) + 1}–{Math.min(currentPage * perPage, total)} / {total}
            </div>
            <Pagination page={currentPage} lastPage={lastPage} onPageChange={setCurrentPage} />
          </div>
        )}
      </div>

      {/* Add Animal Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('add_new_animal', 'animals')}</h3>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleAddAnimal} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('ear_tag', 'animal')}</label>
                  <input
                    type="text"
                    required
                    value={newAnimal.ear_tag}
                    onChange={(e) => setNewAnimal({...newAnimal, ear_tag: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('birth_date', 'animals')}</label>
                  <input
                    type="date"
                    required
                    value={newAnimal.birth_date}
                    onChange={(e) => setNewAnimal({...newAnimal, birth_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telepre érkezés dátuma</label>
                  <input
                    type="date"
                    value={newAnimal.arrival_date}
                    onChange={(e) => setNewAnimal({...newAnimal, arrival_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('gender', 'animals')}</label>
                  <select
                    value={newAnimal.gender}
                    onChange={(e) => setNewAnimal({...newAnimal, gender: e.target.value as 'him' | 'nosteny'})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="him">{t('male', 'animal')}</option>
                    <option value="nosteny">{t('female', 'animal')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('breed', 'animals')}</label>
                  <ClearableSelect
                    value={newAnimal.breed}
                    onChange={(val) => {
                      const breedData = breeds.find(b => b.value === val);
                      const defaultGain = breedData?.daily_weight_gain || 1.1;
                      setNewAnimal({
                        ...newAnimal,
                        breed: val,
                        daily_weight_gain: defaultGain.toString()
                      });
                    }}
                    placeholder="Válassz fajtát..."
                    required
                  >
                    {breeds.map(breed => (
                      <option key={breed.key} value={breed.value}>{breed.display_value || breed.value}</option>
                    ))}
                  </ClearableSelect>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Színe</label>
                  <ClearableSelect
                    value={newAnimal.color}
                    onChange={(val) => setNewAnimal({...newAnimal, color: val})}
                    placeholder="Válassz színt..."
                  >
                    {colors.map(color => (
                      <option key={color.key} value={color.value}>{color.display_value || color.value}</option>
                    ))}
                  </ClearableSelect>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Anya fülszáma</label>
                  <input
                    type="text"
                    value={newAnimal.mother_ear_tag}
                    onChange={(e) => setNewAnimal({...newAnimal, mother_ear_tag: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    placeholder="Pl. HU12345"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Származási ország</label>
                  <ClearableSelect
                    value={newAnimal.origin_country}
                    onChange={(val) => setNewAnimal({...newAnimal, origin_country: val})}
                    placeholder="Válassz országot..."
                  >
                    {countries.map(country => (
                      <option key={country.key} value={country.value}>{country.display_value || country.value}</option>
                    ))}
                  </ClearableSelect>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Származási tenyészet kódja</label>
                  <input
                    type="text"
                    value={newAnimal.origin_farm_code}
                    onChange={(e) => setNewAnimal({...newAnimal, origin_farm_code: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    placeholder="Pl. HU001234"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('purchase_weight', 'animals')} ({t('kg', 'general')}) *</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newAnimal.purchase_weight}
                    onChange={(e) => setNewAnimal({...newAnimal, purchase_weight: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Becsült napi hízási érték (kg/nap) *</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newAnimal.daily_weight_gain}
                    onChange={(e) => setNewAnimal({...newAnimal, daily_weight_gain: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    placeholder="Pl. 1.1"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Az állat fajtája alapján automatikusan kitöltődik, de módosítható</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beszerzési számla sorszáma *</label>
                  <input
                    type="text"
                    value={newAnimal.invoice_number}
                    onChange={(e) => setNewAnimal({...newAnimal, invoice_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    placeholder="Pl. SZ-2024-001"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Értékesítési számla sorszáma</label>
                  <input
                    type="text"
                    value={newAnimal.sale_invoice_number}
                    onChange={(e) => setNewAnimal({...newAnimal, sale_invoice_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    placeholder="Pl. SZ-2024-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telepről kikerülés dátuma</label>
                  <input
                    type="date"
                    value={newAnimal.departure_date}
                    onChange={(e) => setNewAnimal({...newAnimal, departure_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="cattle_document_received"
                    checked={newAnimal.cattle_document_received}
                    onChange={(e) => setNewAnimal({...newAnimal, cattle_document_received: e.target.checked})}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="cattle_document_received" className="ml-2 block text-sm font-medium text-gray-700">
                    Marhalevél fizikailag megérkezett
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('purchase_price', 'animals')} ({t('huf', 'general')})</label>
                  <input
                    type="number"
                    value={newAnimal.purchase_price}
                    onChange={(e) => setNewAnimal({...newAnimal, purchase_price: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    {t('cancel', 'action')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    {t('add', 'action')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Animal Modal */}
      {showEditForm && editingAnimal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('edit_animal', 'animals')}</h2>
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateAnimal(); }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('ear_tag', 'animal')}</label>
                  <input
                    type="text"
                    value={editingAnimal.ear_tag}
                    disabled
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('birth_date', 'animals')}</label>
                  <input
                    type="date"
                    required
                    value={editingAnimal.birth_date?.split('T')[0] || ''}
                    onChange={(e) => setEditingAnimal({...editingAnimal, birth_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telepre érkezés dátuma</label>
                  <input
                    type="date"
                    value={editingAnimal.arrival_date?.split('T')[0] || ''}
                    onChange={(e) => setEditingAnimal({...editingAnimal, arrival_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('gender', 'animals')}</label>
                  <select
                    value={editingAnimal.gender}
                    onChange={(e) => setEditingAnimal({...editingAnimal, gender: e.target.value as 'him' | 'nosteny'})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="him">{t('male', 'animal')}</option>
                    <option value="nosteny">{t('female', 'animal')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('breed', 'animals')}</label>
                  <ClearableSelect
                    value={editingAnimal.breed}
                    onChange={(val) => setEditingAnimal({...editingAnimal, breed: val})}
                    placeholder="Válassz fajtát..."
                    required
                  >
                    {breeds.map(breed => (
                      <option key={breed.key} value={breed.value}>{breed.display_value || breed.value}</option>
                    ))}
                  </ClearableSelect>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Színe</label>
                  <ClearableSelect
                    value={editingAnimal.color || ''}
                    onChange={(val) => setEditingAnimal({...editingAnimal, color: val})}
                    placeholder="Válassz színt..."
                  >
                    {colors.map(color => (
                      <option key={color.key} value={color.value}>{color.display_value || color.value}</option>
                    ))}
                  </ClearableSelect>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Anya fülszáma</label>
                  <input
                    type="text"
                    value={editingAnimal.mother_ear_tag || ''}
                    onChange={(e) => setEditingAnimal({...editingAnimal, mother_ear_tag: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    placeholder="Pl. HU12345"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Származási ország</label>
                  <ClearableSelect
                    value={editingAnimal.origin_country || ''}
                    onChange={(val) => setEditingAnimal({...editingAnimal, origin_country: val})}
                    placeholder="Válassz országot..."
                  >
                    {countries.map(country => (
                      <option key={country.key} value={country.value}>{country.display_value || country.value}</option>
                    ))}
                  </ClearableSelect>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Származási tenyészet kódja</label>
                  <input
                    type="text"
                    value={editingAnimal.origin_farm_code || ''}
                    onChange={(e) => setEditingAnimal({...editingAnimal, origin_farm_code: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    placeholder="Pl. HU001234"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('purchase_weight', 'animals')} ({t('kg', 'general')})</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editingAnimal.purchase_weight || ''}
                    onChange={(e) => setEditingAnimal({...editingAnimal, purchase_weight: e.target.value ? Number(e.target.value) : undefined})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Becsült napi hízási érték (kg/nap)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editingAnimal.daily_weight_gain || ''}
                    onChange={(e) => setEditingAnimal({...editingAnimal, daily_weight_gain: e.target.value ? Number(e.target.value) : 1.1})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    placeholder="Pl. 1.1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Az állat becsült napi súlygyarapodása</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beszerzési számla sorszáma</label>
                  <input
                    type="text"
                    value={editingAnimal.invoice_number || ''}
                    onChange={(e) => setEditingAnimal({...editingAnimal, invoice_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    placeholder="Pl. SZ-2024-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Értékesítési számla sorszáma</label>
                  <input
                    type="text"
                    value={editingAnimal.sale_invoice_number || ''}
                    onChange={(e) => setEditingAnimal({...editingAnimal, sale_invoice_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    placeholder="Pl. SZ-2024-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telepről kikerülés dátuma</label>
                  <input
                    type="date"
                    value={editingAnimal.departure_date || ''}
                    onChange={(e) => setEditingAnimal({...editingAnimal, departure_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="edit_cattle_document_received"
                    checked={editingAnimal.cattle_document_received || false}
                    onChange={(e) => setEditingAnimal({...editingAnimal, cattle_document_received: e.target.checked})}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="edit_cattle_document_received" className="ml-2 block text-sm font-medium text-gray-700">
                    Marhalevél fizikailag megérkezett
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('purchase_price', 'animals')} ({t('huf', 'general')})</label>
                  <input
                    type="number"
                    value={editingAnimal.purchase_price || ''}
                    onChange={(e) => setEditingAnimal({...editingAnimal, purchase_price: e.target.value ? Number(e.target.value) : undefined})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div className="md:col-span-2 flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowEditForm(false); setEditingAnimal(null); }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    {t('cancel', 'action')}
                  </button>
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                  >
                    {t('save', 'action')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Animal Card Modal */}
      {selectedAnimalId && (
        <AnimalCard
          animalId={selectedAnimalId}
          onClose={() => setSelectedAnimalId(null)}
        />
      )}

      {/* Export Modal */}
      <ExportAnimalsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        initialFilters={{
          search: debouncedSearch || undefined,
          status: filters.status,
          gender: filters.gender,
          breed: filters.breed,
          stable_id: filters.stable_id,
          box_ids: filters.box_id ? [filters.box_id] : undefined,
          cattle_passport_status: filters.cattle_passport_status,
        }}
        initialFarmId={selectedFarmId}
      />

      {/* Állat mozgatási modal */}
      {showMoveForm && movingAnimal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('move_animal', 'animals')} - {movingAnimal.ear_tag}
                </h3>
                <button
                  onClick={() => setShowMoveForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telep
                  </label>
                  <select
                    value={moveFarmId || ''}
                    onChange={(e) => handleMoveFarmChange(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Válassz telepet...</option>
                    {moveFarms.map((farm) => (
                      <option key={farm.id} value={farm.id}>{farm.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Istálló
                  </label>
                  <ClearableSelect
                    value={String(moveStableId || '')}
                    onChange={(val) => handleMoveStableChange(val ? parseInt(val) : null)}
                    placeholder="Összes istálló"
                    disabled={!moveFarmId}
                  >
                    {moveStables.map((stable) => (
                      <option key={stable.id} value={stable.id}>{stable.name}</option>
                    ))}
                  </ClearableSelect>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('target_box', 'farms')}
                  </label>
                  <select
                    value={moveData.to_box_id}
                    onChange={(e) => setMoveData({...moveData, to_box_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    disabled={!moveFarmId}
                    required
                  >
                    <option value="">{t('select_box', 'farms')}</option>
                    {filteredMoveBoxes.map((box) => (
                      <option key={box.id} value={box.id}>
                        {box.name} ({box.capacity} {t('capacity_places', 'farms')})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('movement_type', 'animals')}
                  </label>
                  <select
                    value={moveData.movement_type}
                    onChange={(e) => setMoveData({...moveData, movement_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="transfer">{t('transfer', 'animals')}</option>
                    <option value="sale">{t('sale', 'animals')}</option>
                    <option value="slaughter">{t('slaughter', 'animals')}</option>
                    <option value="death">{t('death', 'animals')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('reason_optional', 'animals')}
                  </label>
                  <textarea
                    value={moveData.reason}
                    onChange={(e) => setMoveData({...moveData, reason: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    rows={3}
                    placeholder={t('movement_reason_placeholder', 'animals')}
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowMoveForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  onClick={handleConfirmMove}
                  disabled={!moveData.to_box_id}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {t('move', 'action')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={deleteAnimalId !== null}
        onConfirm={handleDeleteAnimal}
        onCancel={() => setDeleteAnimalId(null)}
        title={t('confirm_delete_animal', 'animals')}
        confirmLabel="Törlés"
      />
    </div>
  );
};

export default Animals;